/**
 * AgentConfigModal — Administration complète d'un agent.
 *
 * Affichage conditionnel selon category :
 *   python_agent   → Aperçu + Config + Prompts + Historique
 *   claude_persona → Aperçu + Prompts
 *   mcp_tool       → Aperçu seul (lecture seule)
 *   celery_task    → Aperçu + Config
 *
 * Bootstrap 5.3 + tokens --hrl-*. Zéro PHI dans les affichages.
 */

import { useEffect, useState } from 'react';
import type { AgentSummary, AgentPrompt, AgentAction } from '../services/agentsAdminService';
import { agentsAdminService } from '../services/agentsAdminService';
import { PromptCreateModal } from './PromptCreateModal';
import { AgentConfigForm } from './AgentConfigForm';

interface Props {
  agent: AgentSummary | null;
  onClose: () => void;
  onSelectAgent: (agent: AgentSummary) => void;
}

type Tab = 'overview' | 'config' | 'prompts' | 'history';

const STATUS_BADGE: Record<string, string> = {
  active:  'bg-success',
  idle:    'bg-secondary',
  error:   'bg-danger',
  stopped: 'bg-warning text-dark',
  planned: 'bg-light text-dark border',
};

const CATEGORY_LABEL: Record<string, string> = {
  python_agent:  'Python',
  claude_persona:'Persona',
  mcp_tool:      'MCP',
  celery_task:   'Celery',
};

function fmt(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

// ── Onglet Aperçu ────────────────────────────────────────────────────────────

function OverviewTab({
  agent,
  onSelectAgent,
  onModeChange,
}: {
  agent: AgentSummary;
  onSelectAgent: (a: AgentSummary) => void;
  onModeChange: (mode: 'propose' | 'autonomous') => void;
}) {
  const [togglingMode, setTogglingMode] = useState(false);
  const [modeError, setModeError] = useState<string | null>(null);
  const isMcp = agent.category === 'mcp_tool';
  const isPersona = agent.category === 'claude_persona';

  async function handleToggleMode() {
    const next = agent.mode === 'propose' ? 'autonomous' : 'propose';
    setTogglingMode(true);
    setModeError(null);
    try {
      await agentsAdminService.patchMode(agent.agent_id, next);
      onModeChange(next);
    } catch (e: unknown) {
      setModeError(e instanceof Error ? e.message : 'Erreur changement mode');
    } finally {
      setTogglingMode(false);
    }
  }

  return (
    <div>
      {modeError && <div className="alert alert-danger py-2 small mb-2">{modeError}</div>}

      {isMcp && (
        <div className="alert alert-light py-2 mb-3 small border">
          <i className="bi bi-info-circle me-1" />
          Outil MCP — configuration en lecture seule. Modification via <code>scripts/mcp/</code>.
        </div>
      )}

      <dl className="row mb-0 small">
        <dt className="col-sm-4 text-muted">ID</dt>
        <dd className="col-sm-8 font-monospace">{agent.agent_id}</dd>

        <dt className="col-sm-4 text-muted">Catégorie</dt>
        <dd className="col-sm-8">
          <span className="badge bg-secondary">{CATEGORY_LABEL[agent.category ?? ''] ?? agent.category ?? '—'}</span>
        </dd>

        {agent.version && (
          <>
            <dt className="col-sm-4 text-muted">Version</dt>
            <dd className="col-sm-8">v{agent.version}</dd>
          </>
        )}

        {agent.trigger_type && (
          <>
            <dt className="col-sm-4 text-muted">Déclencheur</dt>
            <dd className="col-sm-8">
              <span className="badge bg-light text-dark border">{agent.trigger_type}</span>
            </dd>
          </>
        )}

        {agent.tool_ref && (
          <>
            <dt className="col-sm-4 text-muted">Référence</dt>
            <dd className="col-sm-8 font-monospace" style={{ fontSize: '0.72rem' }}>{agent.tool_ref}</dd>
          </>
        )}

        {agent.launch_command && (
          <>
            <dt className="col-sm-4 text-muted">Commande</dt>
            <dd className="col-sm-8 font-monospace" style={{ fontSize: '0.72rem' }}>{agent.launch_command}</dd>
          </>
        )}

        {agent.description && (
          <>
            <dt className="col-sm-4 text-muted">Description</dt>
            <dd className="col-sm-8">{agent.description}</dd>
          </>
        )}

        {agent.capabilities && agent.capabilities.length > 0 && (
          <>
            <dt className="col-sm-4 text-muted">Capacités</dt>
            <dd className="col-sm-8 d-flex flex-wrap gap-1">
              {agent.capabilities.map(c => (
                <span key={c} className="badge bg-light text-dark border">{c}</span>
              ))}
            </dd>
          </>
        )}

        {/* Mode toggle — pas pour MCP */}
        {!isMcp && (
          <>
            <dt className="col-sm-4 text-muted">Mode</dt>
            <dd className="col-sm-8 d-flex align-items-center gap-2">
              <span className={`badge ${agent.mode === 'autonomous' ? 'bg-warning text-dark' : 'bg-info text-dark'}`}>
                {agent.mode}
              </span>
              <button
                className="btn btn-xs btn-outline-secondary btn-sm py-0"
                onClick={handleToggleMode}
                disabled={togglingMode}
                title={`Basculer vers ${agent.mode === 'propose' ? 'autonomous' : 'propose'}`}
              >
                {togglingMode ? <span className="spinner-border spinner-border-sm" /> : '⇄'}
              </button>
              {isPersona && (
                <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                  (propose = gate humain requis)
                </small>
              )}
            </dd>
          </>
        )}

        <dt className="col-sm-4 text-muted">Dernière action</dt>
        <dd className="col-sm-8">
          {agent.last_action_type ?? '—'}
          {agent.last_action_created_at && (
            <small className="text-muted ms-2">{fmt(agent.last_action_created_at)}</small>
          )}
        </dd>
      </dl>

      {agent.children.length > 0 && (
        <div className="mt-3">
          <h6 className="text-muted small text-uppercase mb-2">Sous-agents</h6>
          <div className="d-flex flex-wrap gap-2">
            {agent.children.map(child => (
              <button
                key={child.agent_id}
                className="btn btn-sm btn-outline-secondary"
                onClick={() => onSelectAgent(child)}
              >
                <i className="bi bi-cpu me-1" />
                {child.agent_name ?? child.agent_id}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Onglet Config — délègue à AgentConfigForm (schema-driven) ─────────────────

function ConfigTab({ agentId }: { agentId: string }) {
  return <AgentConfigForm agentId={agentId} />;
}

// ── Onglet Prompts (python_agent + claude_persona) ───────────────────────────

function PromptsTab({ agentId, category }: { agentId: string; category: string | null }) {
  const [prompts, setPrompts] = useState<AgentPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [confirmActivateId, setConfirmActivateId] = useState<string | null>(null);
  const [activating, setActivating] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      setPrompts(await agentsAdminService.listPrompts(agentId));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erreur chargement prompts');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, [agentId]);

  async function handleActivate(promptId: string) {
    setActivating(promptId);
    setError(null);
    try {
      await agentsAdminService.activatePrompt(agentId, promptId);
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erreur activation');
    } finally {
      setActivating(null);
      setConfirmActivateId(null);
    }
  }

  if (loading) return <div className="text-center py-3"><div className="spinner-border spinner-border-sm" /></div>;

  const isPersona = category === 'claude_persona';

  return (
    <div>
      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      {isPersona && (
        <div className="alert alert-light py-2 mb-3 small border">
          <i className="bi bi-info-circle me-1" />
          Persona — le fichier <code>tool_ref</code> est la source git. Les versions DB permettent un override runtime.
        </div>
      )}

      {prompts.length === 0 && (
        <p className="text-muted small">Aucune version de prompt. Créez la première ci-dessous.</p>
      )}

      {/* Liste des versions groupées par task_type */}
      {prompts.map(p => (
        <div
          key={p.id}
          className={`border rounded p-3 mb-2 ${p.is_active ? 'border-success bg-success bg-opacity-10' : ''}`}
        >
          <div className="d-flex justify-content-between align-items-start mb-1">
            <div className="d-flex gap-1 flex-wrap">
              <span className="badge bg-secondary">{p.task_type}</span>
              <span className="badge bg-light text-dark border">v{p.version}</span>
              {p.is_active && <span className="badge bg-success">Actif</span>}
            </div>
            {!p.is_active && (
              confirmActivateId === p.id ? (
                <div className="d-flex gap-1">
                  <span className="small text-muted me-1">Activer et désactiver les autres ?</span>
                  <button
                    className="btn btn-xs btn-success btn-sm py-0"
                    onClick={() => void handleActivate(p.id)}
                    disabled={!!activating}
                  >
                    {activating === p.id ? <span className="spinner-border spinner-border-sm" /> : 'Confirmer'}
                  </button>
                  <button
                    className="btn btn-xs btn-outline-secondary btn-sm py-0"
                    onClick={() => setConfirmActivateId(null)}
                  >
                    Annuler
                  </button>
                </div>
              ) : (
                <button
                  className="btn btn-xs btn-outline-success btn-sm py-0"
                  onClick={() => setConfirmActivateId(p.id)}
                >
                  Activer
                </button>
              )
            )}
          </div>

          <pre className="small mb-1 text-wrap" style={{ whiteSpace: 'pre-wrap', fontSize: '0.78rem', maxHeight: '8rem', overflowY: 'auto' }}>
            {p.prompt_text}
          </pre>

          <small className="text-muted d-flex gap-2 flex-wrap">
            <span>{fmt(p.created_at)}</span>
            {p.author && <span>· par <em>{p.author}</em></span>}
            {p.notes && <span>· {p.notes}</span>}
          </small>
        </div>
      ))}

      <div className="mt-3">
        <button
          className="btn btn-sm btn-outline-primary"
          onClick={() => setShowCreate(true)}
        >
          <i className="bi bi-plus-circle me-1" />
          Nouvelle version
        </button>
      </div>

      {showCreate && (
        <PromptCreateModal
          agentId={agentId}
          onClose={() => setShowCreate(false)}
          onCreated={load}
          onCreate={(payload) => agentsAdminService.createPrompt(agentId, payload).then(() => undefined)}
        />
      )}
    </div>
  );
}

// ── Onglet Historique ────────────────────────────────────────────────────────

function HistoryTab({ agentId }: { agentId: string }) {
  const [actions, setActions] = useState<AgentAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    agentsAdminService.listActions(agentId, 20)
      .then(setActions)
      .catch(e => setError(e.message ?? 'Erreur chargement historique'))
      .finally(() => setLoading(false));
  }, [agentId]);

  if (loading) return <div className="text-center py-3"><div className="spinner-border spinner-border-sm" /></div>;
  if (error) return <div className="alert alert-danger py-2 small">{error}</div>;
  if (actions.length === 0) return <p className="text-muted small">Aucune action enregistrée.</p>;

  return (
    <div className="list-group list-group-flush">
      {actions.map(a => (
        <div key={a.id} className="list-group-item px-0 py-2">
          <div className="d-flex justify-content-between">
            <span className="small fw-medium">{a.action_type}</span>
            {a.result && (
              <span className={`badge ${
                a.result === 'success' ? 'bg-success' :
                a.result === 'error' ? 'bg-danger' : 'bg-warning text-dark'
              }`}>
                {a.result}
              </span>
            )}
          </div>
          {a.entity_type && (
            <small className="text-muted">{a.entity_type}{a.entity_id ? ` · ${a.entity_id}` : ''}</small>
          )}
          <div><small className="text-muted">{fmt(a.created_at)}</small></div>
        </div>
      ))}
    </div>
  );
}

// ── Définition des onglets par category ──────────────────────────────────────

function getAvailableTabs(category: string | null): Tab[] {
  switch (category) {
    case 'python_agent':
      return ['overview', 'config', 'prompts', 'history'];
    case 'claude_persona':
      return ['overview', 'prompts'];
    case 'mcp_tool':
      return ['overview'];
    case 'celery_task':
      return ['overview', 'config'];
    default:
      return ['overview', 'prompts', 'history'];
  }
}

const TAB_LABEL: Record<Tab, string> = {
  overview: 'Aperçu',
  config:   'Config',
  prompts:  'Prompts',
  history:  'Historique',
};

// ── Composant principal ───────────────────────────────────────────────────────

export function AgentConfigModal({ agent, onClose, onSelectAgent }: Props) {
  const [tab, setTab] = useState<Tab>('overview');
  const [currentMode, setCurrentMode] = useState<'propose' | 'autonomous'>(agent?.mode ?? 'propose');

  const availableTabs = getAvailableTabs(agent?.category ?? null);

  useEffect(() => {
    setTab('overview');
    setCurrentMode(agent?.mode ?? 'propose');
  }, [agent?.agent_id, agent?.mode]);

  if (!agent) return null;

  const agentWithMode = { ...agent, mode: currentMode };

  return (
    <div
      className="modal d-block"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <div>
              <h5 className="modal-title mb-0">{agent.agent_name ?? agent.agent_id}</h5>
              <small className="text-muted font-monospace">{agent.agent_id}</small>
            </div>
            <div className="d-flex align-items-center gap-2">
              <span className={`badge ${STATUS_BADGE[agent.status] ?? 'bg-secondary'}`}>
                {agent.status}
              </span>
              <span className={`badge ${currentMode === 'autonomous' ? 'bg-warning text-dark' : 'bg-info text-dark'}`}>
                {currentMode}
              </span>
              <button className="btn-close" onClick={onClose} />
            </div>
          </div>

          <div className="modal-body p-0">
            {/* Onglets adaptés au type */}
            <ul className="nav nav-tabs px-3 pt-2">
              {availableTabs.map(t => (
                <li key={t} className="nav-item">
                  <button
                    className={`nav-link py-1 px-3 ${tab === t ? 'active' : ''}`}
                    onClick={() => setTab(t)}
                  >
                    {TAB_LABEL[t]}
                  </button>
                </li>
              ))}
            </ul>

            <div className="p-3">
              {tab === 'overview' && (
                <OverviewTab
                  agent={agentWithMode}
                  onSelectAgent={onSelectAgent}
                  onModeChange={setCurrentMode}
                />
              )}
              {tab === 'config' && availableTabs.includes('config') && (
                <ConfigTab agentId={agent.agent_id} />
              )}
              {tab === 'prompts' && availableTabs.includes('prompts') && (
                <PromptsTab agentId={agent.agent_id} category={agent.category} />
              )}
              {tab === 'history' && availableTabs.includes('history') && (
                <HistoryTab agentId={agent.agent_id} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
